import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";
import { PRODUCT_ID_TO_TIER, STRIPE_TIERS } from "@/config/stripe-tiers";

interface SubscriptionState {
  subscribed: boolean;
  tier: keyof typeof STRIPE_TIERS | null;
  productId: string | null;
  subscriptionEnd: string | null;
  reviewsUsed: number;
  reviewCredits: number;
  freeReviewsRemaining: number;
  brandReviewsUsed: number;
  freeBrandReviewsRemaining: number;
  currentSkus: number;
  maxSkus: number;
  baseSkus: number;
  extraSkus: number;
  maxExtraSkus: number;
  isAdmin: boolean;
  accountType: "practitioner" | "brand";
  /** The owning account's user id — for team members this is their owner's id. */
  accountOwnerId: string | null;
  /** False when the signed-in user is a team member (not the billing owner). */
  isOwner: boolean;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  subscription: SubscriptionState;
  subscriptionLoading: boolean;
  signOut: () => Promise<void>;
  checkSubscription: () => Promise<void>;
  openCheckout: (priceId: string, opts?: { couponId?: string | null; affiliateCode?: string | null; affiliateName?: string | null }) => Promise<void>;
  openCustomerPortal: () => Promise<void>;
  purchaseCredits: (quantity: number) => Promise<void>;
  purchaseExtraSkus: (quantity: number) => Promise<void>;
}

const defaultSubscription: SubscriptionState = {
  subscribed: false,
  tier: null,
  productId: null,
  subscriptionEnd: null,
  reviewsUsed: 0,
  reviewCredits: 0,
  freeReviewsRemaining: 1,
  brandReviewsUsed: 0,
  freeBrandReviewsRemaining: 1,
  currentSkus: 0,
  maxSkus: 1,
  baseSkus: 1,
  extraSkus: 0,
  maxExtraSkus: 0,
  isAdmin: false,
  accountType: "brand",
  accountOwnerId: null,
  isOwner: true,
};

const LOGIN_NOTIFICATION_KEY = "compliguard:last-notified-signin";

const getLoginNotificationMarker = (user: User) => {
  return user.last_sign_in_at ?? user.updated_at ?? user.created_at ?? "unknown";
};

const shouldNotifyLogin = (user: User) => {
  if (typeof window === "undefined") return true;

  const storageKey = `${LOGIN_NOTIFICATION_KEY}:${user.id}`;
  const nextMarker = getLoginNotificationMarker(user);
  const previousMarker = window.localStorage.getItem(storageKey);

  if (previousMarker === nextMarker) {
    return false;
  }

  window.localStorage.setItem(storageKey, nextMarker);
  return true;
};

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  loading: true,
  subscription: defaultSubscription,
  subscriptionLoading: false,
  signOut: async () => {},
  checkSubscription: async () => {},
  openCheckout: async () => {},
  openCustomerPortal: async () => {},
  purchaseCredits: async () => {},
  purchaseExtraSkus: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionState>(defaultSubscription);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [initialSubscriptionLoaded, setInitialSubscriptionLoaded] = useState(false);

  const checkSubscription = useCallback(async () => {
    if (!session) {
      setSubscription(defaultSubscription);
      setInitialSubscriptionLoaded(false);
      return;
    }

    // Only show loading spinner on the first check, not on background refreshes
    if (!initialSubscriptionLoaded) {
      setSubscriptionLoading(true);
    }
    try {
      const { data, error } = await supabase.functions.invoke("check-subscription");
      
      if (error) {
        const isAuthError = error?.message?.includes("401") || error?.message?.includes("Authentication");
        if (isAuthError) {
          // Token is invalid/expired — sign out to redirect to login
          console.warn("Session expired, signing out.");
          await supabase.auth.signOut();
          return;
        }
        console.error("Error checking subscription:", error);
        return;
      }

      if (data?.error) {
        // Edge function returned an error response (e.g. 401 auth failure)
        if (data.error === "Authentication failed") {
          console.warn("Session expired, signing out.");
          await supabase.auth.signOut();
        }
        return;
      }

      if (data?.subscribed) {
        const tier = data.product_id ? PRODUCT_ID_TO_TIER[data.product_id] : null;
        setSubscription({
          subscribed: true,
          tier,
          productId: data.product_id,
          subscriptionEnd: data.subscription_end,
          reviewsUsed: data.reviews_used ?? 0,
          reviewCredits: data.review_credits ?? 0,
          freeReviewsRemaining: data.free_reviews_remaining ?? 0,
          brandReviewsUsed: data.brand_reviews_used ?? 0,
          freeBrandReviewsRemaining: data.free_brand_reviews_remaining ?? 0,
          currentSkus: data.current_skus ?? 0,
          maxSkus: data.max_skus ?? 1,
          baseSkus: data.base_skus ?? 1,
          extraSkus: data.extra_skus ?? 0,
          maxExtraSkus: data.max_extra_skus ?? 0,
          isAdmin: data.is_admin === true,
          accountType: data.account_type === "practitioner" ? "practitioner" : "brand",
          accountOwnerId: data.account_owner_id ?? user?.id ?? null,
          isOwner: data.is_owner !== false,
        });
      } else {
        setSubscription({
          ...defaultSubscription,
          reviewsUsed: data?.reviews_used ?? 0,
          reviewCredits: data?.review_credits ?? 0,
          freeReviewsRemaining: data?.free_reviews_remaining ?? 1,
          brandReviewsUsed: data?.brand_reviews_used ?? 0,
          freeBrandReviewsRemaining: data?.free_brand_reviews_remaining ?? 1,
          currentSkus: data?.current_skus ?? 0,
          maxSkus: data?.max_skus ?? 1,
          accountType: data?.account_type === "practitioner" ? "practitioner" : "brand",
          accountOwnerId: data?.account_owner_id ?? user?.id ?? null,
          isOwner: data?.is_owner !== false,
        });
      }
    } catch (error) {
      console.error("Error checking subscription:", error);
    } finally {
      setSubscriptionLoading(false);
      setInitialSubscriptionLoaded(true);
    }
  }, [session, initialSubscriptionLoaded]);

  const openCheckout = async (
    priceId: string,
    opts?: { couponId?: string | null; affiliateCode?: string | null; affiliateName?: string | null }
  ) => {
    if (!session) {
      window.location.href = "/signup";
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: {
          priceId,
          couponId: opts?.couponId ?? null,
          affiliateCode: opts?.affiliateCode ?? null,
          affiliateName: opts?.affiliateName ?? null,
        },
      });

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
    }
  };

  const openCustomerPortal = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error) {
      console.error("Error opening customer portal:", error);
    }
  };

  const purchaseCredits = async (quantity: number) => {
    if (!session) {
      window.location.href = "/signup";
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("purchase-credits", {
        body: { quantity },
      });

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error) {
      console.error("Error purchasing credits:", error);
    }
  };

  const purchaseExtraSkus = async (quantity: number) => {
    if (!session) {
      window.location.href = "/signup";
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("purchase-extra-skus", {
        body: { quantity },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error) {
      console.error("Error purchasing extra SKUs:", error);
    }
  };

  useEffect(() => {
    // Track whether we already had a session to distinguish real logins from token refreshes
    let hadSession = false;

    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      hadSession = !!existingSession;
      setSession(existingSession);
      setUser(existingSession?.user ?? null);
      setLoading(false);
    });

    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Handle pending account type from Google OAuth signup
        if (event === "SIGNED_IN" && session?.user) {
          const pendingType = localStorage.getItem("pending_account_type");
          const pendingTier = localStorage.getItem("pending_tier");
          const pendingPriceId = localStorage.getItem("pending_price_id");
          const pendingFirstName = localStorage.getItem("pending_first_name");
          const pendingLastName = localStorage.getItem("pending_last_name");
          const pendingContactNumber = localStorage.getItem("pending_contact_number");
          const pendingReferral = localStorage.getItem("pending_referral_source");
          // Invited Google users have no pending account type, but still have
          // a referral answer that must reach the backend signup alert.
          if (!pendingType && pendingReferral && localStorage.getItem("pending_signup_event")) {
            localStorage.removeItem("pending_referral_source");
            setTimeout(() => {
              supabase.from("profiles").update({ referral_source: pendingReferral.slice(0, 250) })
                .eq("id", session.user.id).then(({ error }) => {
                  if (error) console.error("Could not save signup referral:", error.message);
                });
            }, 0);
          }
          if (pendingType && (pendingType === "practitioner" || pendingType === "brand")) {
            const currentType = session.user.user_metadata?.account_type;
            if (!currentType) {
              localStorage.removeItem("pending_account_type");
              localStorage.removeItem("pending_tier");
              localStorage.removeItem("pending_price_id");
              supabase.auth.updateUser({
                data: {
                  account_type: pendingType,
                  ...(pendingTier ? { pending_tier: pendingTier } : {}),
                  ...(pendingPriceId ? { pending_price_id: pendingPriceId } : {}),
                },
              });
              // Persist the personal details captured at signup onto the profile
              if (pendingFirstName || pendingLastName || pendingContactNumber || pendingReferral) {
                supabase
                  .from("profiles")
                  .update({
                    ...(pendingFirstName ? { first_name: pendingFirstName } : {}),
                    ...(pendingLastName ? { last_name: pendingLastName } : {}),
                    ...(pendingContactNumber ? { contact_number: pendingContactNumber } : {}),
                    ...(pendingReferral ? { referral_source: pendingReferral.slice(0, 250) } : {}),
                  })
                  .eq("id", session.user.id)
                  .then(() => {});
              }
            }
            localStorage.removeItem("pending_first_name");
            localStorage.removeItem("pending_last_name");
            localStorage.removeItem("pending_contact_number");
            localStorage.removeItem("pending_referral_source");
            if (currentType) {
              localStorage.removeItem("pending_account_type");
              localStorage.removeItem("pending_tier");
              localStorage.removeItem("pending_price_id");
            }
          }
        }

        // Fire the deferred Google sign_up event only when OAuth returned with
        // a brand-new account (created within the last 5 minutes). Cancelling
        // the consent screen or signing into an existing account never reaches
        // this, so the conversion stays accurate.
        const pendingSignupEvent = localStorage.getItem("pending_signup_event");
        if (event === "SIGNED_IN" && session?.user && pendingSignupEvent) {
          localStorage.removeItem("pending_signup_event");
          const createdAt = session.user.created_at ? new Date(session.user.created_at).getTime() : 0;
          if (createdAt && Date.now() - createdAt < 5 * 60 * 1000) {
            try {
              trackEvent("sign_up", JSON.parse(pendingSignupEvent));
            } catch {
              // malformed payload — drop it
            }
          }
        }



        // Only notify Slack on genuine sign-ins (not token refreshes)
        if (
          event === "SIGNED_IN" &&
          !hadSession &&
          session?.user?.email &&
          shouldNotifyLogin(session.user)
        ) {
          supabase.functions.invoke("notify-slack", {
            body: { event: "user_login", email: session.user.email },
          }).catch(() => {});
        }
        if (event === "SIGNED_IN") hadSession = true;
        if (event === "SIGNED_OUT") hadSession = false;
      }
    );

    return () => authSubscription.unsubscribe();
  }, []);

  // Check subscription when session changes
  useEffect(() => {
    if (session) {
      checkSubscription();
    } else {
      setSubscription(defaultSubscription);
    }
  }, [session, checkSubscription]);

  // Periodically refresh subscription (every 5 minutes) and when the tab
  // regains focus. This avoids hammering Stripe/DB every 60 seconds per user.
  useEffect(() => {
    if (!session) return;

    const interval = setInterval(checkSubscription, 300000);

    let lastFocusRefresh = Date.now();
    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      // Throttle focus-triggered refreshes to at most once per minute
      if (Date.now() - lastFocusRefresh < 60000) return;
      lastFocusRefresh = Date.now();
      checkSubscription();
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleVisibility);
    };
  }, [session, checkSubscription]);

  const signOut = async () => {
    await supabase.auth.signOut();
    setSubscription(defaultSubscription);
  };

  return (
    <AuthContext.Provider value={{ 
      session, 
      user, 
      loading, 
      subscription,
      subscriptionLoading,
      signOut,
      checkSubscription,
      openCheckout,
      openCustomerPortal,
      purchaseCredits,
      purchaseExtraSkus,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
